if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      // `elements` is a live accessor, not a snapshot taken at upgrade time.
      // The country panel is re-rendered whenever the header is, and the old
      // object went on referencing nodes that had already been thrown away -
      // which is what left the country selector inert after an A/B swap.
      get elements() {
        return {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('[data-localization-button]'),
          panel: this.querySelector('[data-localization-panel]'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('[data-localization-close]'),
          resetButton: this.querySelector('[data-localization-reset]'),
          searchIcon: this.querySelector('[data-localization-search-icon]'),
          liveRegion: this.querySelector('#sr-country-search-results'),
        };
      }

      get header() {
        return document.querySelector('[data-header-wrapper]');
      }

      get menuDrawer() {
        return document.getElementById('menu-drawer');
      }

      get overlay() {
        return this.querySelector('[data-localization-overlay]');
      }

      connectedCallback() {
        // connectedCallback also fires on a plain move; these listeners live on
        // this element and survive it, so bind them only once.
        if (this._bound) return;
        this._bound = true;

        this.mql = window.matchMedia('(min-width: 750px)');

        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));

        // All delegated on this element: every one of these controls is inside
        // the markup that gets replaced, so binding them per element meant the
        // panel stopped responding the first time it was re-rendered.
        ThemeBind.delegate(this, 'click', '[data-localization-button]', this.openSelector.bind(this));
        ThemeBind.delegate(this, 'keyup', 'input[name="country_filter"]', this.filterCountries.bind(this));
        // `focus`/`blur` do not bubble; focusin/focusout are their delegatable
        // equivalents.
        ThemeBind.delegate(this, 'focusin', 'input[name="country_filter"]', this.onSearchFocus.bind(this));
        ThemeBind.delegate(this, 'focusout', 'input[name="country_filter"]', this.onSearchBlur.bind(this));
        ThemeBind.delegate(this, 'keydown', 'input[name="country_filter"]', this.onSearchKeyDown.bind(this));
        ThemeBind.delegate(this, 'click', '[data-localization-close]', this.hidePanel.bind(this));
        ThemeBind.delegate(this, 'click', '[data-localization-reset]', this.resetFilter.bind(this));
        ThemeBind.delegate(this, 'mousedown', '[data-localization-reset]', (event) => event.preventDefault());
        ThemeBind.delegate(this, 'click', 'a', this.onItemClick.bind(this));
      }

      hidePanel() {
        const elements = this.elements;
        if (elements.button) elements.button.setAttribute('aria-expanded', 'false');
        if (elements.panel) elements.panel.setAttribute('hidden', true);
        if (elements.search) {
          elements.search.value = '';
          this.filterCountries();
          elements.search.setAttribute('aria-activedescendant', '');
        }
        document.body.classList.remove('overflow-hidden-mobile');
        const menuDrawer = this.menuDrawer;
        if (menuDrawer) menuDrawer.removeAttribute('data-country-selector-open');
        const overlay = this.overlay;
        if (overlay) overlay.removeAttribute('data-overlay-open');
        const header = this.header;
        if (header) header.preventHide = false;
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          // Re-resolved: the panel can be re-rendered before this runs.
          const search = this.elements.search;
          if (!search) return;
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          search.setAttribute(
            'aria-activedescendant',
            focusedItemIndex > -1 ? focusableItems[focusedItemIndex].id : ''
          );
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        const button = this.elements.button;
        if (!button) return;

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel();
            event.stopPropagation();
            button.focus();
            break;
          case 'SPACE':
            if (button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event, item) {
        // Under delegation `currentTarget` is this element, so the matched <a>
        // comes in as the second argument (or from the event target).
        const link = item || (event.target.closest && event.target.closest('a'));
        if (!link) return;
        event.preventDefault();
        const form = this.querySelector('form');
        const input = this.elements.input;
        if (input) input.value = link.dataset.value;
        if (!form) return;
        // requestSubmit(), not submit(): the programmatic submit() does NOT fire a
        // `submit` event, which silently bypassed the market-persistence listener in
        // theme.liquid. That left a shopper who picked "United Kingdom" from this
        // picker with preferred_market still set to their old market - the head guard
        // then redirected them straight back into it, so the selector could not be
        // used to leave a market at all.
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          // Safari < 16 / older Samsung Internet.
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          if (form.dispatchEvent(submitEvent)) form.submit();
        }
      }

      openSelector() {
        const elements = this.elements;
        if (!elements.button || !elements.panel) return;
        elements.button.focus();
        elements.panel.toggleAttribute('hidden');
        elements.button.setAttribute(
          'aria-expanded',
          (elements.button.getAttribute('aria-expanded') === 'false').toString()
        );
        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }
        if (elements.search && this.mql.matches) {
          elements.search.focus();
        }
        const header = this.header;
        if (this.hasAttribute('data-prevent-hide') && header) {
          header.preventHide = true;
        }
        const menuDrawer = this.menuDrawer;
        if (menuDrawer) menuDrawer.setAttribute('data-country-selector-open', '');
        const overlay = this.overlay;
        if (overlay) overlay.setAttribute('data-overlay-open', '');
      }

      closeSelector(event) {
        if (
          (event.target.hasAttribute && event.target.hasAttribute('data-localization-overlay')) ||
          !this.contains(event.target) ||
          !this.contains(event.relatedTarget)
        ) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        const elements = this.elements;
        if (!elements.search) return;
        const searchValue = this.normalizeString(elements.search.value);
        const popularCountries = this.querySelector('[data-localization-popular]');
        const allCountries = this.querySelectorAll('a');
        let visibleCountries = allCountries.length;

        if (elements.resetButton) {
          if (searchValue) elements.resetButton.setAttribute('data-active', '');
          else elements.resetButton.removeAttribute('data-active');
        }

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const nameEl = item.querySelector('[data-localization-country-name]');
          if (!nameEl) return;
          const countryName = this.normalizeString(nameEl.textContent);
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries++;
          } else {
            item.parentElement.classList.add('hidden');
            visibleCountries--;
          }
        });

        if (elements.liveRegion) {
          elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        const scrollEl = this.querySelector('[data-localization-scroll]');
        if (scrollEl) scrollEl.scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        const search = this.elements.search;
        if (!search) return;
        search.value = '';
        this.filterCountries();
        search.focus();
      }

      onSearchFocus() {
        const searchIcon = this.elements.searchIcon;
        if (searchIcon) searchIcon.setAttribute('data-search-focused', '');
      }

      onSearchBlur() {
        const elements = this.elements;
        if (elements.search && !elements.search.value && elements.searchIcon) {
          elements.searchIcon.removeAttribute('data-search-focused');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
